<script setup lang="ts">
import { useAppState } from '~/composables/useAppState';
import Button from '~/components/atoms/Button.vue';
import TextInput from '~/components/ui/TextInput.vue';
import TextArea from '~/components/ui/TextArea.vue';
import CustomLoader from '~/components/ui/CustomLoader.vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useAnalytics } from '~/composables/useAnalytics';

const { trackSelectionCreated, trackSelectionItemAdded } = useAnalytics();

const appState = useAppState();
const toast = useToast();
const queryClient = useQueryClient();

const artworkId = computed(() => appState.activeArtworkIdForModal.value);

const isCreatingNew = ref(false);
const newSelectionName = ref('');
const newSelectionDescription = ref('');
const isSubmitting = ref(false);
const isInitialLoading = ref(false);


const fetchCollections = async () => {
  const shouldShowLoader = appState.collections.value.length === 0;
  if (shouldShowLoader) {
    isInitialLoading.value = true;
  }
  try {
    const session = useSupabaseSession().value;
    const headers: Record<string, string> = {};
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }

    const { collections } = await $fetch<{ collections: any[] }>("/api/collection", {
      headers,
    });
    appState.collections.value = collections;
  } catch (err) {
    console.error("[SaveToSelectionModal] Failed to fetch collections:", err);
  } finally {
    isInitialLoading.value = false;
  }
};

watch(() => appState.isSaveModalOpen.value, async (isOpen) => {
  if (isOpen) {
    await fetchCollections();
  }
});

const toggleArtworkInCollection = async (clickedCollectionId: string) => {
  if (!artworkId.value || isSubmitting.value) return;

  const clickedCollectionIndex = appState.collections.value.findIndex(c => c.id === clickedCollectionId);
  if (clickedCollectionIndex === -1) return;

  const clickedCollection = appState.collections.value[clickedCollectionIndex];
  const isCurrentlySaved = clickedCollection.items.includes(artworkId.value!);

  isSubmitting.value = true;

  // Optimistic UI updates
  const newCollections = [...appState.collections.value];
  const oldCollectionsSnapshot = [...appState.collections.value]; // For rollback

  const newCollection = { ...clickedCollection };
  newCollection.items = isCurrentlySaved
    ? newCollection.items.filter(id => id !== artworkId.value)
    : [...newCollection.items, artworkId.value];
  newCollections[clickedCollectionIndex] = newCollection;

  appState.collections.value = newCollections;

  try {
    if (isCurrentlySaved) {
      await $fetch('/api/remove-from-collection', {
        method: 'POST',
        body: { id: artworkId.value, collectionId: clickedCollectionId }
      });
    } else {
      await $fetch('/api/add-to-collection', {
        method: 'POST',
        body: { id: artworkId.value, collectionId: clickedCollectionId }
      });

      trackSelectionItemAdded({
        selection_id: clickedCollectionId,
        selection_name: clickedCollection.name,
        artwork_id: artworkId.value,
      });

      toast.add({
        title: `Saved to ${clickedCollection.name}`,
        color: 'success',
        duration: 2000,
      });
    }

    if (artworkId.value) {
      queryClient.invalidateQueries({ queryKey: [`artwork-saves-${artworkId.value}`] });
    }

    queryClient.invalidateQueries({ queryKey: ["collections"] });
    queryClient.invalidateQueries({ queryKey: ["published-selections"] });

    // Auto-close dialog after any action
    appState.isSaveModalOpen.value = false;
  } catch (error) {
    // Rollback
    appState.collections.value = oldCollectionsSnapshot;
    
    toast.add({
      title: "Failed to update selection",
      color: "error",
      duration: 2000,
    });
  } finally {
    isSubmitting.value = false;
  }
};

const createSelection = async () => {
  const trimmedName = newSelectionName.value.trim();
  if (!trimmedName || isSubmitting.value) return;

  // Client-side duplicate check
  const duplicateExists = appState.collections.value.some(
    c => c.name.toLowerCase() === trimmedName.toLowerCase()
  );

  if (duplicateExists) {
    toast.add({
      title: "A selection with this name already exists",
      color: "error",
      duration: 2000,
    });
    return;
  }

  isSubmitting.value = true;
  try {
    const { collection } = await $fetch('/api/collection', {
      method: 'POST',
      body: {
        name: trimmedName,
        description: newSelectionDescription.value.trim() || undefined,
      },
    });

    if (collection) {
      trackSelectionCreated({
        selection_id: collection.id || collection.slug,
        selection_name: trimmedName,
      });
      appState.collections.value.push(collection);
      newSelectionName.value = '';
      newSelectionDescription.value = '';
      isCreatingNew.value = false;
      
      // Optionally auto-add the artwork to this newly created list
      if (artworkId.value) {
        await toggleArtworkInCollection(collection.id);
      } else {
        toast.add({ title: "Selection created", color: "success", duration: 2000 });
      }
    }
  } catch (error: any) {
    toast.add({
      title: error?.data?.message || "Failed to create selection",
      color: "error",
      duration: 2000,
    });
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <UModal v-model:open="appState.isSaveModalOpen.value" title="Save to a selection">
    <template #body>
      <div class="flex flex-col gap-4 max-h-[60vh] overflow-y-auto pr-2">
        
        <CustomLoader v-if="isInitialLoading" class="py-8" />

        <template v-else>
          <div 
            v-for="collection in appState.collections.value" 
            :key="collection.id"
            @click="toggleArtworkInCollection(collection.id)"
            class="flex items-center justify-between p-4 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
            :class="{'opacity-50 pointer-events-none': isSubmitting}"
          >
            <div class="flex flex-col gap-1">
              <span class="font-bold text-base text-on-primary-heading">{{ collection.name }}</span>
              <span class="text-sm text-gray-500">{{ collection.items.length }} saved item{{ collection.items.length !== 1 ? 's' : '' }}</span>
            </div>
            
            <div class="flex items-center justify-center w-6 h-6 rounded-full border border-gray-300">
              <Icon 
                v-if="artworkId && collection.items.includes(artworkId)" 
                name="material-symbols:check-circle" 
                class="text-button-primary text-2xl" 
              />
            </div>
          </div>

          <!-- Create New Selection Section -->
          <div v-if="!isCreatingNew" class="mt-4">
            <Button variant="secondary" class="w-full justify-center" @click="isCreatingNew = true">
              <Icon name="material-symbols:add" />
              Create a new selection
            </Button>
          </div>

          <div v-else class="mt-4 flex flex-col gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <TextInput 
              v-model="newSelectionName" 
              placeholder="e.g. Living Room Art" 
              @keyup.enter="createSelection"
            />
            <TextArea
              v-model="newSelectionDescription"
              rows="2"
              maxlength="500"
              placeholder="Why these works (optional)"
            />
            <div class="flex gap-2">
              <Button variant="secondary" class="flex-1 justify-center" @click="isCreatingNew = false">Cancel</Button>
              <Button variant="primary" class="flex-1 justify-center" :loading="isSubmitting" @click="createSelection">Create</Button>
            </div>
          </div>
        </template>

      </div>
    </template>
  </UModal>
</template>
